import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCommunities, fetchCommunityCategories, createCommunity } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { sfx } from "@/lib/sounds";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { Search, Users, Star, Sparkles, Plus, TrendingUp, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function CommunityCard({ c }) {
  return (
    <Link to={`/communities/${c.slug}`} data-testid={`community-card-${c.slug}`}
          className="group rounded-2xl overflow-hidden glass hover:border-[#FFBF00]/40 transition-all">
      <div className="relative aspect-[3/1] overflow-hidden">
        {c.banner ? (
          <img src={c.banner} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/20 via-black to-[#FF8C00]/10" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {c.featured && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#FFBF00] text-black text-[10px] uppercase tracking-widest font-bold">★ Featured</div>
        )}
        {c.category && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] uppercase tracking-widest">{c.category}</div>
        )}
      </div>
      <div className="p-4 flex items-start gap-3">
        <div className="relative -mt-8 shrink-0">
          {c.avatar ? (
            <img src={c.avatar} alt="" className="w-14 h-14 rounded-xl border-2 border-black object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-xl border-2 border-black bg-gradient-to-br from-[#FFBF00] to-[#FF8C00] grid place-items-center text-black font-hero text-xl">
              {c.name?.[0]?.toUpperCase() || "C"}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-hero text-lg truncate group-hover:text-[#FFBF00] transition-colors">{c.name}</div>
          <div className="text-xs text-white/50 truncate">{c.description || "—"}</div>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1"><Users size={11} /> {c.member_count}</span>
            <span>·</span>
            <span>{c.post_count} posts</span>
            {c.viewer_is_member && (
              <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-400">✓ {c.viewer_role}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function CommunitySkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden glass animate-pulse">
      <div className="aspect-[3/1] bg-white/5" />
      <div className="p-4 flex gap-3">
        <div className="-mt-8 w-14 h-14 rounded-xl bg-white/10 border-2 border-black" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-2/3 bg-white/10 rounded" />
          <div className="h-3 w-full bg-white/5 rounded" />
          <div className="h-3 w-24 bg-white/10 rounded" />
        </div>
      </div>
    </div>
  );
}

function CreateCommunityDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [banner, setBanner] = useState("");
  const [avatar, setAvatar] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const c = await createCommunity({ name, description, category, banner, avatar });
      sfx.success();
      toast.success("Community created", { description: c.name });
      onCreated?.(c);
      onOpenChange(false);
      setName(""); setDescription(""); setCategory(""); setBanner(""); setAvatar("");
    } catch (err) {
      sfx.error();
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-black border-[#FFBF00]/30" data-testid="create-community-dialog">
        <DialogHeader>
          <DialogTitle className="font-hero text-2xl">Start a community</DialogTitle>
          <DialogDescription className="text-white/60 text-xs">Groups fans, players, and creators around a shared interest. You'll be its first owner.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Community name" data-testid="new-community-name"
                 className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
          <textarea rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your community…" data-testid="new-community-desc"
                    className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. Speedrun)"
                   data-testid="new-community-category"
                   className="bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
            <input value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="Avatar URL (optional)"
                   className="bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
          </div>
          <input value={banner} onChange={(e) => setBanner(e.target.value)} placeholder="Banner URL (optional)"
                 className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
          <button type="submit" disabled={busy} data-testid="create-community-submit"
                  className="w-full py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-60">
            {busy ? "Creating…" : "Create Community"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SORTS = [
  { key: "members", label: "Members", icon: Users },
  { key: "activity", label: "Activity", icon: TrendingUp },
  { key: "recent", label: "Newest", icon: Clock },
];

export default function Communities() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("members");
  const [categories, setCategories] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await fetchCommunities({ q, category, sort })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, category, sort]);
  useEffect(() => { fetchCommunityCategories().then(setCategories).catch(() => setCategories([])); }, []);

  const featured = useMemo(() => items.filter(c => c.featured), [items]);
  const rest = useMemo(() => items.filter(c => !c.featured), [items]);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-communities">
      <div className="max-w-7xl mx-auto">
        <SectionHeader eyebrow="Communities" title="Find Your Pack" subtitle="Speedrunners, builders, streamers, PvPers — every crew has a home here." />

        <div className="flex flex-col sm:flex-row gap-3 mb-8" data-testid="communities-controls">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input data-testid="communities-search"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search communities…"
                   className="w-full h-12 pl-11 pr-4 rounded-full glass bg-black/40 border-white/10 outline-none focus:border-[#FFBF00]/40 text-sm" />
          </div>
          <div className="flex gap-2 items-center overflow-x-auto scrollbar-none">
            {SORTS.map(({ key, label, icon: I }) => (
              <button key={key} data-testid={`communities-sort-${key}`}
                      onClick={() => setSort(key)}
                      className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-xs uppercase tracking-widest whitespace-nowrap transition ${
                        sort === key ? "bg-[#FFBF00] text-black font-semibold" : "glass border-white/10 hover:border-[#FFBF00]/40"
                      }`}>
                <I size={12} /> {label}
              </button>
            ))}
          </div>
          {user && (
            <button onClick={() => setCreateOpen(true)} data-testid="new-community-btn"
                    className="inline-flex items-center gap-1.5 px-5 h-12 rounded-full bg-gradient-to-r from-[#FFBF00] to-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold shrink-0">
              <Plus size={14} /> New Community
            </button>
          )}
        </div>

        {categories.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-8" data-testid="communities-categories">
            <button onClick={() => setCategory("")}
                    className={`px-3 py-1.5 rounded-full text-xs ${!category ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}>All categories</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`communities-cat-${c}`}
                      className={`px-3 py-1.5 rounded-full text-xs ${category === c ? "bg-[#FFBF00]/20 text-[#FFBF00] border border-[#FFBF00]/40" : "text-white/60 hover:text-white border border-white/10"}`}>{c}</button>
            ))}
          </div>
        )}

        {featured.length > 0 && !loading && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Star size={14} className="text-[#FFBF00]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Featured</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {featured.map((c) => <CommunityCard key={c.id} c={c} />)}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {loading ? (
            <>
              <CommunitySkeleton /><CommunitySkeleton /><CommunitySkeleton />
            </>
          ) : items.length === 0 ? (
            <div className="col-span-full py-20 text-center" data-testid="communities-empty">
              <Sparkles className="mx-auto text-white/30 mb-3" />
              <div className="font-hero text-2xl">No communities yet</div>
              <div className="text-sm text-white/50 mt-2">Be the first to start one.</div>
            </div>
          ) : (
            rest.map((c) => <CommunityCard key={c.id} c={c} />)
          )}
        </div>
      </div>

      <CreateCommunityDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => load()} />
    </div>
  );
}
